import HorizontalTabs, {
  HorizontalTab,
  HorizontalTabRow,
} from '../../molecules/HorizontalTabs/HorizontalTabs';
import { Skeleton } from '../../atoms/Skeleton/Skeleton';
import styles from './DetailTabs.module.css';

const TAB_SKELETON_ROWS = [
  { level: 0, tabCount: 6 },
  { level: 1, tabCount: 7 },
  { level: 2, tabCount: 3 },
];

/**
 * @returns {JSX.Element}
 */
function DetailTabsSkeleton() {
  return (
    <HorizontalTabs>
      {TAB_SKELETON_ROWS.map(({ level, tabCount }) => (
        <HorizontalTabRow key={level} level={level}>
          {Array.from({ length: tabCount }, (_, index) => (
            <Skeleton
              key={index}
              shape="rectangle"
              width="100px"
              height="24px"
              className={styles['tab-skeleton']}
            />
          ))}
        </HorizontalTabRow>
      ))}
    </HorizontalTabs>
  );
}

/**
 * Detail tabs navigation with support for arbitrary nesting depth.
 * @param {object} props
 * @param {DetailTabItem[][]} props.tabRows - Tab items grouped by display level
 * @param {string[]} props.activeTabIds - Active tab id path from root to current leaf
 * @param {(level: number, tabId: string) => void} props.onTabClick - Tab click handler
 * @param {(level: number, tabId: string) => string | undefined} [props.getTabHref] - URL for modified clicks
 * @param {(level: number, tabId: string) => void} [props.onCloseTab] - Tab close handler
 * @param {(level: number, parentIds: string[]) => React.ReactNode} [props.renderLevelActions] - Actions at end of each level row
 * @param {boolean} [props.isLoading=false] - Loading state to display tab bar skeleton
 * @returns {JSX.Element | null}
 */
function DetailTabs({
  tabRows,
  activeTabIds,
  onTabClick,
  getTabHref,
  onCloseTab,
  renderLevelActions,
  isLoading = false,
}) {
  if (isLoading) {
    return <DetailTabsSkeleton />;
  }

  if (!tabRows.length) return null;

  const [rootRow, ...subRows] = tabRows;
  const canCloseTab = (row) => row.length > 1;

  const renderTab = (tab, level, row) => {
    const isActive = activeTabIds[level] === tab.id;

    return (
      <HorizontalTab
        key={tab.id}
        id={tab.id}
        label={tab.label}
        level={level}
        onClick={() => onTabClick(level, tab.id)}
        href={getTabHref?.(level, tab.id)}
        isActive={isActive}
        onClose={
          canCloseTab(row) && onCloseTab
            ? () => onCloseTab(level, tab.id)
            : undefined
        }
      />
    );
  };

  const renderActions = (level, parentIds) => {
    const actions = renderLevelActions?.(level, parentIds);
    if (!actions) return null;

    return (
      <div className={styles['level-actions']}>
        {actions}
      </div>
    );
  };

  return (
    <HorizontalTabs>
      <HorizontalTabRow level={0} actions={renderActions(0, [])}>
        {rootRow.map((tab) => renderTab(tab, 0, rootRow))}
      </HorizontalTabRow>
      {subRows.map((row, rowIndex) => {
        const level = rowIndex + 1;
        const parentIds = activeTabIds.slice(0, level);

        return (
          <HorizontalTabRow
            key={`sub-row-${level}`}
            level={level}
            actions={renderActions(level, parentIds)}
          >
            {row.map((tab) => renderTab(tab, level, row))}
          </HorizontalTabRow>
        );
      })}
    </HorizontalTabs>
  );
}

export default DetailTabs;
